import React, { useContext } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Container from "@mui/material/Container";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import SettingsContext from "./settingsContext";
import * as api from "../services";
import MutationMessage from "../Components/MutationMessage";
import AppContext from "../appContext";

export default function Profile({ maxWidth }) {
  const {
    app: {
      sessionId,
      project: { id: projectId },
    },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { settings, handleChange, validateInput } = useContext(SettingsContext);
  const passwordRequestMutation = useMutation(() =>
    api.postRequestPasswordReset({ email: settings.email, sessionId })
  );
  const resendVerificationMail = useMutation(() =>
    api.postUserSettings({ ...settings, sessionId, projectId })
  );

  return (
    <div>
      <MutationMessage
        mutation={passwordRequestMutation}
        text={{
          loading: t("common.notifications.sendLoading"),
          success: t("common.notifications.sendSuccess"),
        }}
      />
      <MutationMessage
        mutation={resendVerificationMail}
        text={{
          loading: t("common.notifications.sendLoading"),
          success: t("common.notifications.sendSuccess"),
        }}
      />
      <Container component="main" maxWidth={maxWidth}>
        <CssBaseline />
        <Box
          marginTop={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                name="firstName"
                fullWidth
                id="firstName"
                label={t("account.profile.firstName")}
                value={settings.firstName}
                onChange={handleChange}
                error={!validateInput("firstName").isValid}
                helperText={validateInput("firstName").message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                fullWidth
                id="lastName"
                label={t("account.profile.lastName")}
                name="lastName"
                value={settings.lastName}
                onChange={handleChange}
                error={!validateInput("lastName").isValid}
                helperText={validateInput("lastName").message}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                name="address"
                variant="outlined"
                fullWidth
                id="address"
                label={t("account.profile.address")}
                value={settings.address}
                onChange={handleChange}
                error={!validateInput("address").isValid}
                helperText={validateInput("address").message}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel shrink id="language-selector">
                  {t("account.profile.language")}
                </InputLabel>
                <Select
                  labelId="language-selector"
                  name="lang"
                  value={settings?.lang ?? "en"}
                  onChange={handleChange}
                  label={t("account.profile.language")}
                >
                  <MenuItem value={"en"}>English</MenuItem>
                  <MenuItem value={"nl"}>Nederlands</MenuItem>
                  <MenuItem value={"de"}>Deutsch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                id="email"
                label={t("account.profile.email")}
                name="email"
                value={settings.email}
                disabled={true}
                helperText={
                  <Box marginLeft={-1}>
                    <Button
                      onClick={() => passwordRequestMutation.mutate()}
                      variant="body2"
                    >
                      {t("account.profile.resetPassword")}
                    </Button>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                id="email2"
                label={t("account.profile.email2")}
                name="email2"
                value={settings.email2}
                onChange={handleChange}
                error={!validateInput("email2").isValid}
                helperText={
                  !validateInput("email2").isValid ? (
                    <Box>
                      {validateInput("email2").message}
                      <Typography>
                        <Link
                          onClick={() => resendVerificationMail.mutate()}
                          variant="body2"
                          href="#"
                        >
                          {t("account.profile.resetVerification")}
                        </Link>
                      </Typography>
                    </Box>
                  ) : null
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </div>
  );
}
